import React, { useEffect } from 'react';
import useInputHelper from '../inputHelper';
import { useUiTheme } from 'framework/uiTheme';
import useFormValidation from '../../useFormValidation';
import useInputList, { InputListHelperProps } from './inputListHelper';
import { FormFieldsData } from '../../FormContext';
import {
  DefaultInputLayoutProps,
  InputLayoutHintProps,
  InputLayoutStateProps,
  ValidationLayoutProps,
  DefaultInputPropTypes,
} from '../types';
import { SxProps } from '@mui/material';

export type InputListTemplateProps = DefaultInputPropTypes<any> & { isLast?: boolean } & Record<string, any>;

export interface InputWrapperProps {
  source: string;
  inputTemplate: React.FC<InputListTemplateProps>;
  addText?: string;
  deleteText?: string;
}

interface BaseInputListProps {
  inputTemplate: React.FC<InputListTemplateProps>;
  addText?: string;
  deleteText?: string;
}

export interface InputListLayoutProps
  extends
    BaseInputListProps,
    DefaultInputLayoutProps<string | object>,
    InputLayoutHintProps,
    InputLayoutStateProps,
    ValidationLayoutProps {
  fields: (string | object)[];
  addMethod: () => void;
  removeMethod: (index: number) => void;
  onChangeCallback: (index: number, value: FormFieldsData) => void;
  maxFields: number;
  minFields: number;
  simpleLayout?: boolean;
  inputProps?: DefaultInputPropTypes<string | object> | Record<string, any>;
  isAddable?: boolean;
  isDeletable?: boolean;
  disabledRemove?: (value: FormFieldsData) => boolean;
  removeButtonTooltipe?: (value: FormFieldsData) => React.ReactNode;
  wrapperClassName?: string;
  rowClassName?: string;
  rowSx?: SxProps;
}

export type InputListLayoutType = ReactComponent<InputListLayoutProps>;

export interface InputListProps extends BaseInputListProps, InputListHelperProps {
  label?: TypeOrFunction<string | JSX.Element> | Nullable<string>;
  isAddable?: boolean;
  isDeletable?: boolean;
  CustomInputListLayout?: ReactComponent<InputListLayoutProps> | null;
  disabledRemove?: (value: FormFieldsData | any) => boolean;
  removeButtonTooltipe?: (value: FormFieldsData | any) => React.ReactNode;
  inputProps?: DefaultInputPropTypes<string | object> | Record<string, any>;
  simpleLayout?: boolean;
  wrapperClassName?: string;
  rowClassName?: string;
  rowSx?: SxProps;
}

const InputList: React.FC<InputListProps> = ({
  inputTemplate,
  addText,
  deleteText = '',
  maxFields: initialMaxFields = null,
  minFields: initialMinFields = null,
  defaultValue = '',
  CustomInputListLayout = null,
  ...props
}: InputListProps) => {
  let { InputListLayout } = useUiTheme<InputListLayoutType>();
  if (CustomInputListLayout !== null) {
    InputListLayout = CustomInputListLayout;
  }
  const inputHelper = useInputHelper(props);
  const value = inputHelper.getValue() || '';
  const source = inputHelper.getSource();
  const validationCallback = inputHelper.getValidationCallback();
  const { getMaxElements, getMinElements } = useFormValidation(source);
  const maxFields = initialMaxFields || getMaxElements();
  const minFields = initialMinFields || getMinElements();

  const { onChangeCallback, addInput, removeInput } = useInputList({
    maxFields: initialMaxFields,
    minFields: initialMinFields,
    defaultValue,
    ...props,
  });

  useEffect(() => {
    validationCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(value as object[]).length]);

  return (
    <InputListLayout
      {...props}
      value={inputHelper.getValue()}
      className={inputHelper.getClassName()}
      isVisible={inputHelper.isVisible()}
      maxFields={maxFields}
      minFields={minFields}
      addMethod={addInput}
      removeMethod={removeInput}
      inputTemplate={inputTemplate}
      fields={(value as object[]) || []}
      onChangeCallback={onChangeCallback}
      addText={addText}
      deleteText={deleteText}
      isRequired={inputHelper.getIsRequired()}
      errorMessages={inputHelper.getErrorMessages()}
      disabled={!!inputHelper.getIsDisabled()}
    />
  );
};

export default InputList;
